import { Injectable, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { catchError, tap, finalize, shareReplay, take } from 'rxjs/operators';
import { of, Observable } from 'rxjs';

@Injectable({
	providedIn: 'root',
})
export class UserService {
	userDetails = signal<any>(null);
	private userInfoRequest$: Observable<any> | null = null;

	constructor(private http: HttpClient, private router: Router) {}

	getUserInfo$() {
		const token = localStorage.getItem('authToken');

		if (!token) {
			this.clearUserDataAndRedirect();
			return of(null);
		}

		// If user details already loaded, return them
		if (this.userDetails()) {
			return of(this.userDetails());
		}

		// If there's already a pending request, return it (share the request)
		if (this.userInfoRequest$) {
			return this.userInfoRequest$;
		}

		const apiUrl = `${environment.apiUrl}/user`;

		// Create a new request and cache it
		this.userInfoRequest$ = this.http.get<any>(apiUrl).pipe(
			tap((response) => {
				if (response.status && response.data) {
					this.userDetails.set(response.data);
				}
				// Check if response is the user object directly (has id and email)
				else if (response?.id && response?.email) {
					console.log('UserService - Setting user from direct response');
					this.userDetails.set(response);
				}
				// Invalid response
				else {
					console.log('UserService - Invalid response, clearing data');
					this.clearUserDataAndRedirect();
				}
			}),
			catchError((error: HttpErrorResponse) => {
				console.error('Error fetching user info:', error);

				// If unauthorized (401) or forbidden (403), clear token and redirect
				if (error.status === 401 || error.status === 403) {
					this.clearUserDataAndRedirect();
				}

				return of(null);
			}),
			finalize(() => {
				// Clear the request cache after completion
				this.userInfoRequest$ = null;
			}),
			shareReplay(1), // Share the result with all subscribers
			take(1) // Complete after first emission
		);

		return this.userInfoRequest$;
	}

	clearUserDataAndRedirect() {
		localStorage.removeItem('authToken');
		this.userDetails.set(null);
		this.userInfoRequest$ = null; // Clear any pending request
		this.router.navigate(['/login']);
	}

	logout$() {
		const apiUrl = `${environment.apiUrl}/logout`;
		return this.http.post(apiUrl, {});
	}

	logout() {
		const apiUrl = `${environment.apiUrl}/logout`;

		this.http.post(apiUrl, {}).subscribe({
			next: () => {
				this.clearUserDataAndRedirect();
			},
			error: () => {
				// Even if API call fails, clear local data
				this.clearUserDataAndRedirect();
			},
		});
	}

	isAuthenticated(): boolean {
		return !!localStorage.getItem('authToken');
	}

	isAdmin(): boolean {
		const user = this.userDetails();
		console.log('Checking if user is admin:', user);
		console.log('User role:', user?.role);
		// Check both 'name' field and if role_id is 1 or 2 (Super Admin or Admin)
		return (
			user?.role?.name?.toLowerCase() === 'admin' ||
			user?.role?.name?.toLowerCase() === 'super admin' ||
			user?.role_id === 1 ||
			user?.role_id === 2
		);
	}

	getUserRole(): string | null {
		const user = this.userDetails();
		return user?.role?.name || null;
	}
}
